/**
 * Who is this money going to?
 *
 * The donation dialog used to show only "Support My Sport" and a grid of
 * amounts -- nothing named the athlete. A donor who arrived from a shared link,
 * scrolled, and tapped Support saw a payment form with no person attached to it.
 *
 * The NAME resolves for anonymous visitors (it falls back to missionpages.title,
 * which is publicly readable). The PHOTO does not yet: users/{uid} is
 * `allow get: if isSignedIn()`, so a logged-out donor gets 403 and photoURL is
 * empty -- see TSF-238. Hence the initials fallback, which is a real design, not
 * a placeholder: it keeps the badge meaningful today and fills in on its own
 * once the public projection lands.
 */

export function initialsFor(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    // Drop tokens that start with punctuation -- "Milan & Mason Bell" must read
    // "MM", not "M&". Real creator names in this database contain &, -, and emoji.
    .filter((w) => /^[\p{L}\p{N}]/u.test(w))
    .slice(0, 2)
    .map((w) => [...w][0] ?? '')
    .join('')
    .toUpperCase();
}

interface CreatorBadgeProps {
  name: string;
  photoURL?: string;
  className?: string;
}

export default function CreatorBadge({ name, photoURL, className = '' }: CreatorBadgeProps) {
  if (!name?.trim()) return null;
  return (
    <div className={`flex items-center justify-center gap-2 ${className}`}>
      <span className="h-8 w-8 shrink-0 rounded-full overflow-hidden bg-gray-100 ring-1 ring-gray-200 flex items-center justify-center">
        {photoURL ? (
          // alt="" on purpose: the name sits right next to it, so a screen
          // reader would otherwise announce the person twice.
          <img src={photoURL} alt="" className="h-full w-full object-cover" />
        ) : (
          <span className="text-xs font-semibold text-gray-500">{initialsFor(name)}</span>
        )}
      </span>
      <span className="text-sm text-gray-600 truncate">{name}</span>
    </div>
  );
}
